import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useSectionMotion, EASE } from './motion';
import Resplandor from './Resplandor';
import Frase from './Frase';
import Muro, { FILAS as FILAS_MURO, POR_FILA as POR_FILA_MURO } from './Muro';
import Marca from './Marca';
import { useIdioma } from '../../i18n/idioma';

/* Los diez sitios donde vive hoy el negocio de un contratista, escritos como los
   diría él: «recibos en la troca» y «servilletas» son literales — así se hacen
   muchos estimados. */
/* Claves, no texto: el arreglo vive en el módulo y ahí no hay hook. */
const HERRAMIENTAS = [
    'lp.reemplaza.h.excel', 'lp.reemplaza.h.whatsapp', 'lp.reemplaza.h.cuaderno',
    'lp.reemplaza.h.word', 'lp.reemplaza.h.recibos', 'lp.reemplaza.h.fotos',
    'lp.reemplaza.h.calendario', 'lp.reemplaza.h.llamadas', 'lp.reemplaza.h.notas',
    'lp.reemplaza.h.servilletas',
] as const;

/**
 * El problema, dicho en voz baja — y resuelto delante de tus ojos.
 *
 * HISTORIA, para que nadie la repita mal: aquí hubo un muro de diez palabras a
 * 48 px en gris, tachadas con líneas amarillas gordas que se dibujaban con el
 * scroll. El equipo lo señaló como una de las tres cosas «espantosas» de la
 * mitad de arriba, y se sustituyó por una lista tranquila.
 *
 * Ahora vuelve el muro, pero es otra cosa. Lo que fallaba de aquel no era la
 * idea de tachar: era el VOLUMEN. Diez palabras enormes y diez trazos del
 * color de marca compiten con el titular, con el fondo y entre ellas. Aquí las
 * herramientas son textura —pequeñas, grises, repetidas hasta llenar el
 * ancho—, el tachón es un filete fino del color del propio texto, y el peso
 * visual se lo lleva lo único que queda en pie cuando el resto está tachado.
 *
 * El gesto viene del «Appear Text» de Originkit: una rejilla que se borra
 * hasta dejar una sola palabra en el centro. Ver Muro.tsx para lo que se le
 * cambió a aquel componente y por qué.
 */
const Reemplaza: React.FC = () => {
    const { t } = useIdioma();
    /* Memorizado, y no por ahorrar diez traducciones: este arreglo baja a
       `Muro`, que lo usa como dependencia para armar sus filas. Sin memorizar,
       cada render manda un arreglo nuevo y Muro rehace todo aunque las palabras
       sean las mismas. Es exactamente lo que dejó clavada la palabra que rota
       en Producto (ver el comentario de OficioQueCambia), así que aquí se corta
       de raíz. Depende de `t`, que cambia solo al cambiar de idioma. */
    const herramientas = React.useMemo(() => HERRAMIENTAS.map((c) => t(c)), [t]);
    const scope = useSectionMotion<HTMLElement>(({ gsap, ScrollTrigger, scope, reveal, titular }) => {
        /* El titular entra palabra por palabra. Es el gesto más visible de la
           página y por eso se reserva a los enunciados grandes: si se usara en
           todos los textos, dejaría de significar nada. */
        titular({ start: 'top 78%' });

        /* ── El muro se vacía y se rehace, solo ──────────────────
           Va en BUCLE, como el «Appear Text» del que sale, no atado al scroll.
           Lo estuvo, y fue una decisión mía equivocada: lo até al scroll por
           ahorrarme el coste de una animación permanente, y el resultado era
           que había que bajar con la mano a la velocidad justa para verlo.

           El coste sí se paga, pero solo donde toca: el ciclo está PAUSADO
           mientras el fold no está en pantalla, así que no gira gastando
           batería al fondo de una página que nadie está mirando. Y no hay
           `pin`: si la animación no la mueve el scroll, clavar la sección solo
           serviría para no dejar pasar.

           El ciclo, de 5,3 s:
              0,0  se tachan las diez y el muro se acerca
              1,0  se BORRAN, del centro hacia fuera
              1,5  entra la marca, desenfocada
              2,2  remanso: solo la marca
              3,4  la marca se va
              3,7  vuelven las palabras y se destachan
              4,6  respiro, y otra vez */
        {
            const muro = scope.querySelector('[data-muro]');
            if (!muro) return;

            const campo = muro.querySelector<HTMLElement>('.rp-muro__campo');
            const remate = muro.querySelector<HTMLElement>('[data-remate]');
            const palabras = gsap.utils.toArray<HTMLElement>('[data-muro-palabra]', muro);
            const tachones = gsap.utils.toArray<HTMLElement>('[data-tachon]', muro);
            const entero = 'inset(0% 0% 0% 0%)';
            const fuera = (el: HTMLElement) =>
                el.dataset.sentido === 'rtl' ? 'inset(0% 100% 0% 0%)' : 'inset(0% 0% 0% 100%)';

            gsap.set(tachones, { scaleX: 0 });
            gsap.set(palabras, { clipPath: entero });
            gsap.set(remate, { autoAlpha: 0, filter: 'blur(18px)', scale: 0.9 });

            const tl = gsap.timeline({ repeat: -1, paused: true });

            tl.to(
                tachones,
                {
                    scaleX: 1,
                    duration: 0.5,
                    ease: EASE.none,
                    stagger: { each: 0.008, from: 'random' },
                },
                0,
            )
                .to(campo, { scale: 1.06, duration: 1, ease: 'power1.inOut' }, 0)

                /* El gesto que importa: cada palabra se RECORTA hasta
                   desaparecer, no se apaga. Un fundido deja una mancha donde
                   estaba el texto; el recorte lo barre. Renglones pares hacia
                   la derecha, impares hacia la izquierda, y el orden sale de la
                   rejilla desde el centro: el hueco se abre justo donde va a
                   aparecer la marca. */
                .to(
                    palabras,
                    {
                        clipPath: (_i: number, el: HTMLElement) => fuera(el),
                        duration: 0.5,
                        ease: 'power2.inOut',
                        stagger: { grid: [FILAS_MURO, POR_FILA_MURO], from: 'center', each: 0.05 },
                    },
                    1,
                )
                .to(campo, { scale: 1, duration: 1, ease: 'power1.inOut' }, 1)
                .to(
                    remate,
                    { autoAlpha: 1, filter: 'blur(0px)', scale: 1, duration: 0.7, ease: EASE.out },
                    1.5,
                )

                // Remanso: la marca, sola, el tiempo suficiente para leerla.
                .to(
                    remate,
                    {
                        autoAlpha: 0,
                        filter: 'blur(14px)',
                        scale: 0.94,
                        duration: 0.5,
                        ease: 'power2.in',
                    },
                    3.4,
                )

                // Y el muro se rehace, esta vez desde los bordes hacia dentro.
                .to(
                    palabras,
                    {
                        clipPath: entero,
                        duration: 0.5,
                        ease: 'power2.out',
                        stagger: { grid: [FILAS_MURO, POR_FILA_MURO], from: 'edges', each: 0.045 },
                    },
                    3.7,
                )
                .to(
                    tachones,
                    {
                        scaleX: 0,
                        duration: 0.4,
                        ease: EASE.none,
                        stagger: { each: 0.006, from: 'random' },
                    },
                    3.9,
                )
                .to({}, { duration: 0.7 }, 4.6);

            /* La deriva de los renglones va aparte y a su ritmo: si compartiera
               la línea de tiempo del ciclo, se reiniciaría de golpe en cada
               vuelta y se vería el salto. */
            const deriva = gsap.timeline({ paused: true });
            gsap.utils.toArray<HTMLElement>('[data-muro-fila]', muro).forEach((fila, i) => {
                const sentido = i % 2 === 0 ? 1 : -1;
                const fuerza = 26 + (i % 3) * 22;
                deriva.fromTo(
                    fila,
                    { x: -sentido * fuerza },
                    {
                        x: sentido * fuerza,
                        duration: 7 + (i % 3) * 1.5,
                        ease: 'sine.inOut',
                        repeat: -1,
                        yoyo: true,
                    },
                    0,
                );
            });

            /* Solo gira mientras se ve. Es lo que hace que un bucle infinito
               sea aceptable en mitad de una página. */
            const arranca = (si: boolean) => {
                if (si) {
                    tl.play();
                    deriva.play();
                } else {
                    tl.pause();
                    deriva.pause();
                }
            };
            const st = ScrollTrigger.create({
                trigger: muro,
                start: 'top bottom',
                end: 'bottom top',
                onToggle: (self) => arranca(self.isActive),
            });
            // `onToggle` no dispara si al crearse ya estaba dentro del encuadre.
            if (st.isActive) arranca(true);
        }

        reveal('[data-fade]', { start: 'top 65%' });
    });

    return (
        <section
            ref={scope}
            /* `relative z-10`: este fold es el que sube por encima de la
               portada pegada. Sin z propio, el elemento `sticky` se pintaría
               encima de él. */
            data-fondo="navy"
            className="relative z-10 overflow-hidden bg-navy-850 px-5 py-[clamp(5.5rem,12vw,10rem)] sm:px-8"
        >
            <Resplandor />

            <div className="relative mx-auto max-w-[1240px]">
                <h2 className="rp-h2 max-w-[16ch] text-white">
                    <Frase marca="titular">{t('lp.reemplaza.titular')}</Frase>
                </h2>

                <Muro
                    palabras={herramientas}
                    remate="Raza Pro"
                    lectura={t('lp.muro.lectura', { lista: herramientas.join(', '), remate: 'Raza Pro' })}
                />

                <div
                    data-fade
                    className="mt-[clamp(2rem,5vw,3.5rem)] flex flex-col items-start gap-7 border-t border-white/10 pt-12 sm:flex-row sm:items-end sm:justify-between"
                >
                    {/* Vuelve a decir el nombre, y a tamaño de titular.
                        Cuando el muro se recorría con el scroll, «Raza Pro» se
                        quedaba puesto en el centro y este renglón podía ser un
                        remate pequeño que continuaba la frase. Con el muro en
                        BUCLE ya no: el nombre solo está en pantalla un cuarto
                        de cada vuelta, así que «Los reemplaza todos» se quedaba
                        huérfano —sin sujeto— el resto del tiempo, y a 2 rem
                        pasaba desapercibido justo donde hay que rematar el
                        argumento del fold. */}
                    {/* El nombre va en amarillo y el resto en blanco. Sobre este
                        navy-850 el amarillo da 8:1 (el blanco, 12:1): los dos
                        pasan de sobra, así que la elección es de composición, no
                        de legibilidad. Lo que aporta es que el acento caiga en
                        la MARCA y no en la frase entera. Pintar las dos líneas
                        de amarillo también se leería, pero es el enunciado más
                        grande del fold y ahí compite con el CTA. */}
                    <p className="rp-h2 max-w-[16ch] text-white">
                        <Marca>{t('lp.reemplaza.remate')}</Marca>
                    </p>
                    <a href="#producto" className="rp-link shrink-0 text-brand-400">
                        {t('lp.reemplaza.enlace')}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </a>
                </div>
            </div>
        </section>
    );
};

export default Reemplaza;
